import { endOfWeek, format, parseISO, startOfMonth, startOfWeek } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/** Presets de período para o componente visual `SeletorPeriodo`. */
export type TipoPeriodoPreset =
  | 'ultimos30'
  | 'ultimos60'
  | 'ultimos90'
  | 'ultimoAno'
  | 'semFiltro'
  | 'noDia'
  | 'naSemana'
  | 'noMes'
  | 'intervalo'

/** Opções exibidas como chips no `SeletorPeriodo`. */
export const PRESETS_PERIODOS: { id: TipoPeriodoPreset; label: string }[] = [
  { id: 'ultimos30', label: 'Últimos 30 dias' },
  { id: 'ultimos60', label: 'Últimos 60 dias' },
  { id: 'ultimos90', label: 'Últimos 90 dias' },
  { id: 'ultimoAno', label: 'Último ano' },
  { id: 'semFiltro', label: 'Sem filtro de data' },
  { id: 'noDia', label: 'No dia' },
  { id: 'naSemana', label: 'Na semana' },
  { id: 'noMes', label: 'No mês' },
  { id: 'intervalo', label: 'Período personalizado' },
]

/** Resumo uma linha para o estado colapsado (filtro atual). */
export function rotuloResumoSeletorPeriodo(
  tipo: TipoPeriodoPreset,
  _dataReferenciaIso: string,
  intervaloInicioIso: string,
  intervaloFimIso: string,
): string {
  if (tipo === 'intervalo')
  {
    const di = intervaloInicioIso.trim()
    const df = intervaloFimIso.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(di))
    {
      const parteIni = format(parseISO(di), 'dd/MM/yyyy', { locale: ptBR })
      const parteFim = /^\d{4}-\d{2}-\d{2}$/.test(df)
        ? format(parseISO(df), 'dd/MM/yyyy', { locale: ptBR })
        : '…'
      return `Personalizado (${parteIni} – ${parteFim})`
    }
    return 'Período personalizado'
  }
  const encontrado = PRESETS_PERIODOS.find((p) => p.id === tipo)
  return encontrado?.label ?? String(tipo)
}

/** Converte preset + dados auxiliares em `dataInicio` / `dataFim` (ISO `yyyy-MM-dd`). Semana começa na segunda-feira. */
export function resolverDatasPeriodo(
  tipo: TipoPeriodoPreset,
  dataReferenciaIso: string,
  intervaloInicioIso: string,
  intervaloFimIso: string,
  agoraRef: Date = new Date(),
): { dataInicio: string; dataFim: string } {
  const hoje = new Date(agoraRef.getFullYear(), agoraRef.getMonth(), agoraRef.getDate())
  const fimPadrao = format(hoje, 'yyyy-MM-dd')

  switch (tipo) {
    case 'ultimos30': {
      const ini = new Date(hoje)
      ini.setDate(ini.getDate() - 29)
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: fimPadrao }
    }
    case 'ultimos60': {
      const ini = new Date(hoje)
      ini.setDate(ini.getDate() - 59)
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: fimPadrao }
    }
    case 'ultimos90': {
      const ini = new Date(hoje)
      ini.setDate(ini.getDate() - 89)
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: fimPadrao }
    }
    case 'ultimoAno': {
      const ini = new Date(hoje)
      ini.setFullYear(ini.getFullYear() - 1)
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: fimPadrao }
    }
    case 'semFiltro':
      return { dataInicio: '2000-01-01', dataFim: fimPadrao }
    case 'noDia': {
      let ref = hoje
      if (dataReferenciaIso && /^\d{4}-\d{2}-\d{2}$/.test(dataReferenciaIso))
        ref = parseISO(dataReferenciaIso)
      const s = format(ref, 'yyyy-MM-dd')
      return { dataInicio: s, dataFim: s }
    }
    case 'naSemana': {
      let ref = hoje
      if (dataReferenciaIso && /^\d{4}-\d{2}-\d{2}$/.test(dataReferenciaIso))
        ref = parseISO(dataReferenciaIso)
      const ini = startOfWeek(ref, { weekStartsOn: 1 })
      const fim = endOfWeek(ref, { weekStartsOn: 1 })
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: format(fim, 'yyyy-MM-dd') }
    }
    case 'noMes': {
      let ref = hoje
      if (dataReferenciaIso && /^\d{4}-\d{2}-\d{2}$/.test(dataReferenciaIso))
        ref = parseISO(dataReferenciaIso)
      const ini = startOfMonth(ref)
      const fimUltimo = new Date(ref.getFullYear(), ref.getMonth() + 1, 0)
      return {
        dataInicio: format(ini, 'yyyy-MM-dd'),
        dataFim: format(fimUltimo, 'yyyy-MM-dd'),
      }
    }
    case 'intervalo': {
      const di = intervaloInicioIso?.trim()
      const df = intervaloFimIso?.trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(di!) && /^(\d{4}-\d{2}-\d{2})$/.test(df!))
        return { dataInicio: di!, dataFim: df || fimPadrao }
      if (/^\d{4}-\d{2}-\d{2}$/.test(di!) && !df)
        return { dataInicio: di!, dataFim: fimPadrao }
      return { dataInicio: format(startOfMonth(hoje), 'yyyy-MM-dd'), dataFim: fimPadrao }
    }
    default: {
      const ini = new Date(hoje)
      ini.setDate(ini.getDate() - 29)
      return { dataInicio: format(ini, 'yyyy-MM-dd'), dataFim: format(hoje, 'yyyy-MM-dd') }
    }
  }
}
